import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Wallet, 
  TrendingUp, 
  AlertTriangle, 
  Clock, 
  Gamepad2,
  Trophy,
  Gift,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  FileCheck,
  DollarSign,
  Globe,
  Shield,
  Activity,
  Eye,
  Percent,
  UserPlus,
  CreditCard,
  Target,
  BarChart3,
  PieChart as PieChartIcon,
  Bell,
  ChevronRight,
  Filter,
  Calendar,
  Play,
  Pause,
  MapPin,
  Zap,
  UserCheck,
  UserX,
  Banknote,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingDown
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { dashboardAPI } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EF4444', '#EC4899'];

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, change, changeType, color = "primary", onClick, subtitle }) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-500/10 text-emerald-400",
    warning: "bg-amber-500/10 text-amber-400",
    danger: "bg-red-500/10 text-red-400",
    info: "bg-blue-500/10 text-blue-400",
    purple: "bg-violet-500/10 text-violet-400",
  };

  return (
    <div 
      className={`stat-card ${onClick ? 'cursor-pointer hover:border-primary/50' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className={`widget-icon ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {change !== undefined && (
          <div className={`widget-change ${changeType === 'positive' ? 'positive' : 'negative'}`}>
            {changeType === 'positive' ? (
              <ArrowUpRight className="w-3 h-3" />
            ) : (
              <ArrowDownRight className="w-3 h-3" />
            )}
            {change}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="stat-value text-2xl">{value}</p>
        <p className="stat-label mt-1">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {onClick && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

// Section Header Component
const SectionHeader = ({ title, icon: Icon, linkTo, linkText, onLinkClick }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-primary" />}
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
    {linkTo && (
      <Button variant="ghost" size="sm" onClick={onLinkClick} className="text-primary hover:text-primary/80">
        {linkText || 'View All'}
        <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    )}
  </div>
);

// Activity Feed Item
const ActivityItem = ({ type, message, time, userId }) => {
  const typeConfig = {
    registration: { icon: UserPlus, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    deposit: { icon: Wallet, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    withdrawal: { icon: CreditCard, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    big_win: { icon: Trophy, color: 'text-primary', bg: 'bg-primary/10' },
    fraud: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
    kyc: { icon: FileCheck, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    bonus: { icon: Gift, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  };
  
  const config = typeConfig[type] || typeConfig.registration;
  const Icon = config.icon;
  
  return (
    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
      <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">{message}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">{time}</span>
          {userId && <span className="text-xs text-muted-foreground font-mono">ID: {userId}</span>}
        </div>
      </div>
    </div>
  );
};

// Fraud Alert Item
const FraudAlertItem = ({ alert, onAction }) => {
  const severityColors = {
    critical: 'border-red-500/50 bg-red-500/5',
    high: 'border-orange-500/50 bg-orange-500/5',
    medium: 'border-amber-500/50 bg-amber-500/5',
    low: 'border-blue-500/50 bg-blue-500/5',
  };
  
  return (
    <div className={`p-3 rounded-lg border ${severityColors[alert.severity] || severityColors.medium}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`badge border ${alert.severity === 'critical' ? 'badge-danger' : alert.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'badge-warning'}`}>
              {alert.severity}
            </span>
            <span className="text-sm font-medium">{alert.type}</span>
          </div>
          <p className="text-xs text-muted-foreground">{alert.description}</p>
          <p className="text-xs text-muted-foreground font-mono mt-1">User: {alert.user_id?.slice(0, 12)}... | Score: {alert.risk_score}</p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction('review', alert)}>
            <Eye className="w-3 h-3 mr-1" />
            Review
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dateRange, setDateRange] = useState('today');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await dashboardAPI.getStats(dateRange);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchCharts = useCallback(async () => {
    try {
      const [revenueRes, usersRes, gamesRes] = await Promise.all([
        dashboardAPI.getRevenueChart(30),
        dashboardAPI.getUsersChart(30),
        dashboardAPI.getGamesChart(30),
      ]);
      setRevenueData(revenueRes.data);
      setUsersData(usersRes.data);
      setGamesData(gamesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard charts:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(() => {
    fetchStats();
    fetchCharts();
  }, [fetchStats, fetchCharts]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchCharts();
  }, [fetchCharts]);

  // Auto-refresh
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStats, 10000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStats]);

  const handleFraudAction = () => {
    navigate('/fraud');
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm font-mono" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.value > 1000 ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const canViewFinancials = true;
  const canViewFraud = true;
  const canViewUsers = true;
  const activityFeed = (stats?.activity || []).map((item) => ({
    ...item,
    time: item.time ? new Date(item.time).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '',
    userId: item.user_id,
  }));
  const fraudAlerts = stats?.fraud_alerts || [];
  const variantTotal = (stats?.variants || []).reduce((sum, row) => sum + (row.games || 0), 0) || 1;
  const gameVariantData = (stats?.variants || []).map((row) => ({
    name: String(row.name || 'other').replace(/_/g, ' '),
    value: Math.round(((row.games || 0) / variantTotal) * 100),
    games: row.games,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Live players, money, and tables{admin?.name ? ` · ${admin.name}` : ''}</p>
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36" data-testid="date-range-filter">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
            <Zap className={`w-4 h-4 ${autoRefresh ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className="text-sm text-muted-foreground">Auto-refresh</span>
            <Switch 
              checked={autoRefresh} 
              onCheckedChange={setAutoRefresh}
              data-testid="auto-refresh-toggle"
            />
          </div>

          <Button variant="outline" onClick={fetchDashboardData} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* User Metrics Section */}
      {canViewUsers && (
        <div>
          <SectionHeader 
            title="User Metrics" 
            icon={Users} 
            linkTo="/users" 
            linkText="Manage Users"
            onLinkClick={() => navigate('/users')}
          />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <KPICard
              title="Total Users"
              value={formatNumber(stats?.total_users || 0)}
              icon={Users}
              color="primary"
              onClick={() => navigate('/users')}
            />
            <KPICard
              title="Active Today"
              value={formatNumber(stats?.active_users_today || 0)}
              icon={Activity}
              color="success"
              subtitle="Sessions"
            />
            <KPICard
              title="Sessions 30d"
              value={formatNumber(stats?.mau || 0)}
              icon={TrendingUp}
              color="info"
            />
            <KPICard
              title="New in period"
              value={formatNumber(stats?.new_users || 0)}
              icon={UserPlus}
              color="success"
              onClick={() => navigate('/users')}
            />
            <KPICard
              title="Banned"
              value={formatNumber(stats?.banned_users || 0)}
              icon={UserX}
              color="danger"
              onClick={() => navigate('/users')}
            />
            <KPICard
              title="KYC Pending"
              value={stats?.pending_kyc || 0}
              icon={Clock}
              color="warning"
              onClick={() => navigate('/kyc')}
            />
            <KPICard
              title="Suspicious"
              value={stats?.suspicious_accounts || 0}
              icon={UserX}
              color="danger"
              onClick={() => navigate('/fraud')}
            />
          </div>
        </div>
      )}

      {/* Financial Metrics Section */}
      {canViewFinancials && (
        <div>
          <SectionHeader 
            title="Financial Metrics" 
            icon={DollarSign} 
            linkTo="/wallet" 
            linkText="View Transactions"
            onLinkClick={() => navigate('/wallet')}
          />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <KPICard
              title="Total Deposits"
              value={formatCurrency(stats?.total_deposits || 0)}
              icon={Wallet}
              color="success"
              subtitle="Approved"
              onClick={() => navigate('/wallet')}
            />
            <KPICard
              title="Total Withdrawals"
              value={formatCurrency(stats?.total_withdrawals || 0)}
              icon={CreditCard}
              color="warning"
              subtitle="Approved"
              onClick={() => navigate('/wallet')}
            />
            <KPICard
              title="Net Flow"
              value={formatCurrency(stats?.net_flow || 0)}
              icon={TrendingUp}
              color="success"
              onClick={() => navigate('/wallet')}
            />
            <KPICard
              title="Rake Collected"
              value={formatCurrency(stats?.total_rake || 0)}
              icon={Percent}
              color="primary"
              onClick={() => navigate('/rake')}
            />
            <KPICard
              title="Bonus Issued"
              value={formatCurrency(stats?.total_bonus_issued || 0)}
              icon={Gift}
              color="purple"
              onClick={() => navigate('/bonuses')}
            />
            <KPICard
              title="Deposits waiting"
              value={stats?.pending_deposits || 0}
              icon={Clock}
              color="warning"
              subtitle={formatCurrency(stats?.pending_deposit_amount || 0)}
              onClick={() => navigate('/wallet')}
            />
            <KPICard
              title="Pending Payouts"
              value={stats?.pending_withdrawals || 0}
              icon={Clock}
              color="warning"
              onClick={() => navigate('/wallet')}
            />
          </div>
        </div>
      )}

      {/* Compliance Metrics */}
      {canViewFinancials && (
        <div>
          <SectionHeader 
            title="Compliance & Tax (India)" 
            icon={FileCheck} 
            linkTo="/countries" 
            linkText="View Settings"
            onLinkClick={() => navigate('/countries')}
          />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard
              title="GST Collected"
              value={formatCurrency(stats?.gst_collected || 0)}
              icon={Banknote}
              color="info"
              subtitle="28% on entry fee"
              onClick={() => navigate('/reports')}
            />
            <KPICard
              title="Player balances"
              value={formatCurrency((stats?.deposit_liability || 0) + (stats?.winnings_liability || 0))}
              icon={FileCheck}
              color="purple"
              subtitle="30% on winnings"
              onClick={() => navigate('/reports')}
            />
            <KPICard
              title="Pending payouts"
              value={formatCurrency(stats?.pending_withdrawal_amount || 0)}
              icon={AlertCircle}
              color="warning"
            />
            <KPICard
              title="Open fraud alerts"
              value={stats?.active_fraud_alerts || 0}
              icon={AlertTriangle}
              color="danger"
              onClick={() => navigate('/fraud')}
            />
            <KPICard
              title="Live players"
              value={stats?.live_players || 0}
              icon={Bell}
              color="warning"
              onClick={() => navigate('/fraud')}
            />
          </div>
        </div>
      )}

      {/* Charts Row 1 - Financial */}
      {canViewFinancials && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="chart-container cursor-pointer hover:border-primary/30" onClick={() => navigate('/reports')}>
            <div className="chart-header">
              <h3 className="chart-title">Revenue Overview (30 Days)</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
                <BarChart3 className="w-4 h-4" />
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(value) => value.slice(5)} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                <Tooltip content={customTooltip} />
                <Area type="monotone" dataKey="deposits" stroke="#10B981" fillOpacity={1} fill="url(#colorDeposits)" name="Deposits" />
                <Area type="monotone" dataKey="withdrawals" stroke="#F59E0B" fillOpacity={1} fill="url(#colorWithdrawals)" name="Withdrawals" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container cursor-pointer hover:border-primary/30" onClick={() => navigate('/rake')}>
            <div className="chart-header">
              <h3 className="chart-title">Rake Collection (30 Days)</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/rake')}>
                <TrendingUp className="w-4 h-4" />
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(value) => value.slice(5)} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                <Tooltip content={customTooltip} />
                <Line type="monotone" dataKey="rake" stroke="#F59E0B" strokeWidth={2} dot={false} name="Rake" />
                <Line type="monotone" dataKey="bonus" stroke="#8B5CF6" strokeWidth={2} dot={false} name="Bonus" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Charts Row 2 - Users & Games */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canViewUsers && (
          <div className="chart-container cursor-pointer hover:border-primary/30" onClick={() => navigate('/users')}>
            <div className="chart-header">
              <h3 className="chart-title">Daily Active Users</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
                <Users className="w-4 h-4" />
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={usersData.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(value) => value.slice(8)} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip content={customTooltip} />
                <Bar dataKey="dau" fill="#F59E0B" radius={[4, 4, 0, 0]} name="DAU" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="chart-container cursor-pointer hover:border-primary/30" onClick={() => navigate('/games')}>
          <div className="chart-header">
            <h3 className="chart-title">Games by mode</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/games')}>
              <PieChartIcon className="w-4 h-4" />
            </Button>
          </div>
          {gameVariantData.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No completed games in this period</div>
          ) : (
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={gameVariantData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {gameVariantData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Share']} />
            </PieChart>
          </ResponsiveContainer>
          )}
        </div>

        <div className="chart-container cursor-pointer hover:border-primary/30" onClick={() => navigate('/games')}>
          <div className="chart-header">
            <h3 className="chart-title">Game Volume (30 Days)</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/games')}>
              <Gamepad2 className="w-4 h-4" />
            </Button>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={gamesData.slice(-14)}>
              <defs>
                <linearGradient id="colorGames" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(value) => value.slice(8)} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
              <Tooltip content={customTooltip} />
              <Area type="monotone" dataKey="tables_created" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorGames)" name="Tables" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Game Analytics Section */}
      <div>
        <SectionHeader 
          title="Game Analytics" 
          icon={Gamepad2} 
          linkTo="/games" 
          linkText="Manage Games"
          onLinkClick={() => navigate('/games')}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <KPICard
            title="Games in period"
            value={formatNumber(stats?.games_in_period || 0)}
            icon={Gamepad2}
            color="primary"
            onClick={() => navigate('/games')}
          />
          <KPICard
            title="Active Tables"
            value={stats?.active_tables || 0}
            icon={Activity}
            color="success"
            onClick={() => navigate('/games')}
          />
          <KPICard
            title="Waiting tables"
            value={stats?.waiting_tables || 0}
            icon={Clock}
            color="info"
          />
          <KPICard
            title="Live games"
            value={stats?.live_games || 0}
            icon={TrendingDown}
            color="warning"
          />
          <KPICard
            title="Tournaments"
            value={stats?.active_tournaments || 0}
            icon={Trophy}
            color="purple"
            onClick={() => navigate('/tournaments')}
          />
          <KPICard
            title="Connections"
            value={stats?.ws_connections || 0}
            icon={XCircle}
            color="danger"
          />
        </div>
      </div>

      {canViewFinancials && (
        <div className="chart-container">
          <SectionHeader 
            title="Games by mode" 
            icon={Gamepad2} 
            linkTo="/games" 
            linkText="Open games"
            onLinkClick={() => navigate('/games')}
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Mode</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Games</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Entry fees</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Share</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.variants || []).length === 0 ? (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No completed games in this period</td></tr>
                ) : (stats.variants || []).map((row) => (
                  <tr key={row.name} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => navigate('/games')}>
                    <td className="py-3 px-4 font-medium capitalize">{String(row.name).replace(/_/g, ' ')}</td>
                    <td className="text-right py-3 px-4 font-mono">{formatNumber(row.games || 0)}</td>
                    <td className="text-right py-3 px-4 font-mono text-emerald-400">{formatCurrency(row.fees || 0)}</td>
                    <td className="text-right py-3 px-4 font-mono">{Math.round(((row.games || 0) / variantTotal) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom Section: Fraud Alerts & Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fraud & Risk Alerts */}
        {canViewFraud && (
          <div className="chart-container">
            <SectionHeader 
              title="Fraud & Risk Alerts" 
              icon={Shield} 
              linkTo="/fraud" 
              linkText="View All Alerts"
              onLinkClick={() => navigate('/fraud')}
            />
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {fraudAlerts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No open fraud alerts.</p>
              ) : fraudAlerts.map((alert) => (
                <FraudAlertItem key={alert.id} alert={alert} onAction={handleFraudAction} />
              ))}
            </div>
          </div>
        )}

        {/* Real-Time Activity Feed */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Real-Time Activity</h2>
              {autoRefresh && (
                <span className="badge badge-success text-xs animate-pulse">Live</span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {activityFeed.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent deposits, withdrawals, signups, or games.</p>
            ) : activityFeed.map((activity, idx) => (
              <ActivityItem key={idx} {...activity} />
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
